import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/jetbrains-mono/500.css';

import { createTheme, Theme, alpha } from '@mui/material/styles';

import { selectPalette, lightPalette, ColorMode } from './palettes';
import { MOBILE_DENSITY, phoneMedia } from './mobileDensity';

/**
 * Clarity theme for the farchat UI.
 *
 * Bright, airy, modern-SaaS identity (the calm of Linear / Stripe). Pulls its
 * color tokens from `./palettes` (light + dark) and pairs a characterful
 * geometric display face (Sora, loaded via the Google Fonts <link> in
 * public/index.html) — used SPARINGLY on headings — with the highly-legible
 * Inter for body/UI and JetBrains Mono for codes (NAICS/PSC/FAR refs/prices).
 *
 * Exported as a factory so tests and hosts can construct fresh instances
 * without relying on module-level singleton state. The `mode` selects the
 * light or dark palette; shape/typography/shadows are identical across modes
 * (colors & fonts only — hard constraint).
 */
export function createAppTheme(mode: ColorMode = 'light'): Theme {
  // Characterful display face used sparingly on large headings only. Falls back
  // to Inter (always available via @fontsource) until Sora finishes loading.
  const displayFont =
    '"Sora", Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  const base = createTheme({
    palette: selectPalette(mode),
    shape: {
      // Gently rounded corners (Clarity). Bumped 10 → 12 for a softer, airier
      // silhouette across cards, papers, inputs, and menus.
      borderRadius: 12,
    },
    typography: {
      fontFamily:
        'Inter, Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      // Custom monospace family for code surfaces (passes through MUI's
      // createTypography via ...other; type-augmented in theme/types.d.ts).
      // COLORS & FONTS ONLY — does not affect the Inter UI family or type scale.
      fontFamilyMonospace:
        '"JetBrains Mono", source-code-pro, Menlo, Monaco, Consolas, "Courier New", monospace',
      // Headings use the Sora display face for a calm, premium character; the
      // airy scale is a touch larger with tighter tracking and roomier
      // line-height so titles breathe (Clarity direction).
      h1: {
        fontFamily: displayFont,
        fontSize: '34px',
        lineHeight: '42px',
        fontWeight: 600,
        letterSpacing: '-0.02em',
      },
      h2: {
        fontFamily: displayFont,
        fontSize: '27px',
        lineHeight: '36px',
        fontWeight: 600,
        letterSpacing: '-0.015em',
      },
      h3: {
        fontFamily: displayFont,
        fontSize: '22px',
        lineHeight: '30px',
        fontWeight: 600,
        letterSpacing: '-0.01em',
      },
      h4: {
        fontFamily: displayFont,
        fontSize: '19px',
        lineHeight: '28px',
        fontWeight: 600,
        letterSpacing: '-0.005em',
      },
      // h5/h6 stay on Inter — they are small UI labels, not display headings.
      h5: {
        fontSize: '16px',
        lineHeight: '24px',
        fontWeight: 600,
      },
      h6: {
        fontSize: '14px',
        lineHeight: '22px',
        fontWeight: 600,
      },
      body1: {
        fontSize: '15px',
        lineHeight: '23px',
      },
      body2: {
        fontSize: '14px',
        lineHeight: '21px',
      },
      subtitle1: {
        fontSize: '14px',
        lineHeight: '20px',
        fontWeight: 500,
      },
      subtitle2: {
        fontSize: '12px',
        lineHeight: '18px',
        fontWeight: 500,
      },
      button: {
        fontWeight: 600,
        letterSpacing: '0',
      },
      overline: {
        fontSize: '11px',
        lineHeight: '16px',
        fontWeight: 600,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
      },
      display: {
        fontFamily: displayFont,
        fontSize: '30px',
        lineHeight: '38px',
        fontWeight: 600,
        fontFeatureSettings: '"tnum"',
      },
    },
  });

  // Soft, diffuse, low elevation scale (Clarity). Replaces the previously
  // flattened low levels with gentle cool-tinted shadows so cards, the navbar,
  // menus, and the chat surface lift quietly off the airy canvas. Levels 5+
  // keep MUI defaults.
  const shadows = [...base.shadows] as Theme['shadows'];
  // Keep the existing desktop/print scale; phones need less display type,
  // not smaller body copy. These variants also reach non-PageHeader consumers.
  const phone = phoneMedia(base);
  const compactHeading = (variant: 'h1' | 'h2' | 'h3', fontSize: string, lineHeight: string) => ({
    ...base.typography[variant],
    [phone]: { fontSize, lineHeight },
    '@media print': {
      fontSize: base.typography[variant].fontSize,
      lineHeight: base.typography[variant].lineHeight,
    },
  });
  shadows[1] = '0 1px 2px rgba(16,24,40,0.05)';
  shadows[2] = '0 2px 8px rgba(16,24,40,0.06)';
  shadows[3] = '0 6px 16px rgba(16,24,40,0.08)';
  shadows[4] = '0 10px 28px rgba(16,24,40,0.09)';

  return createTheme(base, {
    shadows,
    typography: {
      h1: compactHeading('h1', '28px', '34px'),
      h2: compactHeading('h2', '24px', '32px'),
      h3: compactHeading('h3', '20px', '28px'),
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          // Bare (non-MUI) <textarea> surfaces — e.g. the `Box component="textarea"`
          // controls used by several stages (GenerateQuestions, Section C/D/E,
          // CLS, VendorDiscovery, …) — otherwise default to the UA white page
          // background with dark text, which renders WRONG in dark mode. Pin
          // them to theme tokens so they follow the active color mode.
          //
          // The `:not(.MuiInputBase-input)` guard excludes MUI multiline
          // TextField inner textareas (which MUI keeps transparent so their
          // rounded InputBase root shows through) — we must not tint those.
          //
          // The always-light DOCX/ProseMirror editor canvases that mirror a
          // printed page are wrapped in <ForceLightTheme>, so this rule
          // (resolved against the active theme) keeps them light there too.
          'textarea:not(.MuiInputBase-input)': {
            backgroundColor: base.palette.background.paper,
            color: base.palette.text.primary,
            caretColor: base.palette.text.primary,
            '&::placeholder': {
              color: base.palette.text.secondary,
              opacity: base.palette.mode === 'dark' ? 0.65 : 0.7,
            },
            '&:disabled': {
              color: base.palette.text.disabled,
            },
          },
          body: {
            backgroundColor: base.palette.background.default,
            color: base.palette.text.primary,
            // Drive the UA color-scheme from the active mode so NATIVE form
            // controls (bare <input type="date"/number/text>, <select>,
            // scrollbars, the native date-picker popup) render dark in dark
            // mode instead of the default light UA appearance. Without this,
            // un-MUI'd native inputs (e.g. the Usage Dashboard date fields)
            // stay light-on-light in dark mode. ForceLightTheme subtrees
            // (always-light DOCX/editor canvases) contain no native inputs, so
            // a global value here is safe.
            colorScheme: base.palette.mode === 'dark' ? 'dark' : 'light',
            // Mode-aware CSS custom properties consumed by the global rules in
            // index.css (SOW annotation/diff washes, TBD highlight). Because
            // `base` is rebuilt per-mode by createAppTheme(mode), these flip with
            // the active theme. COLORS ONLY. The always-light editor canvas rules
            // in index.css deliberately do NOT read these vars (see spec §8b).
            '--sow-added': alpha(base.palette.diff.added, 0.2),
            '--sow-removed': alpha(base.palette.diff.removed, 0.12),
            '--sow-selected': alpha(base.palette.diff.selected, 0.3),
            '--sow-selected-strong': base.palette.diff.selected,
            '--sow-added-strong': base.palette.diff.added,
            '--sow-removed-strong': base.palette.diff.removed,
            '--tbd-color': base.palette.diff.removed,
            // Theme-aware tokens for the Section B–E TipTap editor canvas
            // (.ProseMirror). CSS can't read MUI palette directly, so we expose
            // these and consume them in index.css. They flip with the active
            // mode (base is rebuilt per-mode by createAppTheme), so the editor
            // field is dark in dark mode. NOTE: the always-light DOCX viewer
            // (.sow-docx-viewer) is a separate, deliberately-light canvas and
            // does not consume these.
            '--editor-bg': base.palette.background.paper,
            '--editor-text': base.palette.text.primary,
            '--editor-border': base.palette.divider,
            '--editor-table-header-bg': base.palette.surfaceMuted,
            '--annotation-highlight':
              base.palette.mode === 'dark'
                ? alpha(base.palette.warning.main, 0.25)
                : alpha('#FFEB3B', 0.3),
            '--annotation-highlight-strong':
              base.palette.mode === 'dark'
                ? alpha(base.palette.warning.main, 0.4)
                : alpha('#FFEB3B', 0.5),
          },
          // Pin annotation CSS vars to their LIGHT-mode values inside the
          // always-light DOCX viewer container. <ForceLightTheme> only swaps
          // the MUI ThemeProvider — it cannot override CSS custom properties
          // that cascade from `body`. Without this, dark-mode body vars leak
          // into the viewer and distort annotation highlight colors.
          '.sow-docx-viewer': {
            '--sow-added': alpha(lightPalette.diff!.added as string, 0.2),
            '--sow-removed': alpha(lightPalette.diff!.removed as string, 0.12),
            '--sow-selected': alpha(lightPalette.diff!.selected as string, 0.3),
            '--sow-selected-strong': lightPalette.diff!.selected,
            '--sow-added-strong': lightPalette.diff!.added,
            '--sow-removed-strong': lightPalette.diff!.removed,
            '--tbd-color': lightPalette.diff!.removed,
            '--annotation-highlight': alpha('#FFEB3B', 0.3),
            '--annotation-highlight-strong': alpha('#FFEB3B', 0.5),
          },
          // Accessibility floor: a clearly visible keyboard-focus ring on every
          // interactive surface. `:focus-visible` only triggers for keyboard
          // navigation, so mouse clicks stay ring-free. Uses the indigo accent.
          'a:focus-visible, button:focus-visible, [role="button"]:focus-visible, [tabindex]:focus-visible, .MuiButtonBase-root:focus-visible':
            {
              outline: `2px solid ${base.palette.primary.main}`,
              outlineOffset: '2px',
              borderRadius: '6px',
            },
        },
      },
      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
          outlined: {
            borderColor: base.palette.divider,
          },
        },
      },
      MuiCard: {
        defaultProps: {
          variant: 'outlined',
        },
        styleOverrides: {
          root: {
            borderColor: base.palette.divider,
            borderRadius: 14,
            // Smooth, restrained micro-interaction primitive shared by all
            // ~52 stage cards: border/shadow/transform ease together so any
            // card that opts into a hover lift (e.g. the home cards) feels
            // calm and consistent. Transform settling is owned by MotionCard.
            transition:
              'border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease',
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        // Preserve MUI's relative sizing while reclaiming one pixel above and
        // below each text label. Variant rules also survive nested themes that
        // add their own Button root styles.
        variants: [
          { props: { variant: 'text', size: 'small' }, style: { paddingTop: '3px', paddingBottom: '3px' } },
          { props: { variant: 'text', size: 'medium' }, style: { paddingTop: '5px', paddingBottom: '5px' } },
          { props: { variant: 'text', size: 'large' }, style: { paddingTop: '7px', paddingBottom: '7px' } },
          { props: { variant: 'contained', size: 'small' }, style: { paddingTop: '3px', paddingBottom: '3px' } },
          { props: { variant: 'contained', size: 'medium' }, style: { paddingTop: '5px', paddingBottom: '5px' } },
          { props: { variant: 'contained', size: 'large' }, style: { paddingTop: '7px', paddingBottom: '7px' } },
          { props: { variant: 'outlined', size: 'small' }, style: { paddingTop: '2px', paddingBottom: '2px' } },
          { props: { variant: 'outlined', size: 'medium' }, style: { paddingTop: '4px', paddingBottom: '4px' } },
          { props: { variant: 'outlined', size: 'large' }, style: { paddingTop: '6px', paddingBottom: '6px' } },
        ],
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 10,
            paddingLeft: '16px',
            paddingRight: '16px',
            transition:
              'background-color 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease',
            // Dark-mode outlined primary buttons: thicker border + subtle bg tint
            ...(base.palette.mode === 'dark' && {
              '&.MuiButton-outlined': {
                borderWidth: '1.5px',
                backgroundColor: alpha(base.palette.primary.main, 0.08),
              },
            }),
          },
          // Quiet shadow lift on the primary CTA — the confident accent gets a
          // gentle sense of depth on hover without becoming loud.
          containedPrimary: {
            '&:hover': {
              boxShadow: `0 6px 16px ${alpha(base.palette.primary.main, 0.28)}`,
            },
          },
        },
      },
      MuiAppBar: {
        defaultProps: {
          elevation: 0,
          color: 'inherit',
        },
        styleOverrides: {
          root: {
            backgroundColor: base.palette.background.paper,
            backgroundImage: 'none',
            color: base.palette.text.primary,
            borderBottom: `1px solid ${base.palette.divider}`,
            boxShadow: '0 1px 2px rgba(16,24,40,0.05)',
          },
        },
      },
      MuiChip: {
        defaultProps: {
          size: 'small',
        },
        styleOverrides: {
          root: {
            borderRadius: 6,
            // Dark-mode outlined chips: subtle primary bg tint for visibility
            ...(base.palette.mode === 'dark' && {
              '&.MuiChip-outlined': {
                backgroundColor: alpha(base.palette.primary.main, 0.08),
              },
            }),
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
          size: 'small',
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: { [phone]: { minHeight: MOBILE_DENSITY.touchTarget, minWidth: MOBILE_DENSITY.touchTarget } },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: { [phone]: { fontSize: MOBILE_DENSITY.inputFontSize } },
        },
      },
      // A populated outlined select otherwise relies on browser/MUI timing to
      // float its label. That can leave a server default visually overlapping
      // the label on first paint. Keep labels on the notch globally: this also
      // covers hand-built FormControl + Select pairs outside the GPC forms.
      MuiInputLabel: {
        defaultProps: {
          shrink: true,
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: base.palette.divider,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: base.palette.text.secondary,
            },
            // Stripe-like soft focus ring: the indigo border plus a low-alpha
            // indigo halo make the focused field unmistakable (and AA-visible).
            '&.Mui-focused': {
              boxShadow: `0 0 0 3px ${alpha(base.palette.primary.main, 0.16)}`,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: base.palette.primary.main,
              borderWidth: 1,
            },
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: base.palette.surfaceMuted,
            '& .MuiTableCell-head': {
              fontSize: '11px',
              lineHeight: '16px',
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: base.palette.text.secondary,
            },
          },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            border: `1px solid ${base.palette.divider}`,
            '--DataGrid-rowHoverBackground': base.palette.surfaceMuted,
          },
          row: {
            minHeight: '52px !important',
            maxHeight: '52px !important',
            '&:hover': {
              backgroundColor: base.palette.surfaceMuted,
            },
          },
          columnHeaders: {
            backgroundColor: base.palette.surfaceMuted,
          },
        },
      } as any,
      MuiStepper: {
        styleOverrides: {
          root: {
            backgroundColor: 'transparent',
          },
        },
      },
      MuiStepIcon: {
        styleOverrides: {
          root: {
            color: base.palette.divider,
            '&.Mui-active': {
              color: base.palette.primary.main,
            },
            '&.Mui-completed': {
              color: base.palette.text.secondary,
            },
          },
        },
      },
      MuiStepConnector: {
        styleOverrides: {
          line: {
            borderColor: base.palette.divider,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 18,
          },
        },
      },
      MuiPopover: {
        defaultProps: {
          disableScrollLock: true,
        },
      },
      MuiMenu: {
        defaultProps: {
          disableScrollLock: true,
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: base.palette.text.primary,
            color: base.palette.background.paper,
            borderRadius: 6,
            fontSize: '12px',
            padding: '6px 10px',
          },
          arrow: {
            color: base.palette.text.primary,
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          // body2 is the "secondary body" variant — slightly dimmer than body1.
          // Using the theme token instead of a hardcoded hex so it adapts to
          // light/dark mode.
          body2: {
            color: base.palette.text.secondary,
          },
        },
      },
      MuiLink: {
        defaultProps: {
          underline: 'hover',
        },
        styleOverrides: {
          root: {
            color: base.palette.primary.main,
          },
        },
      },
    },
  });
}

export default createAppTheme;
